'use client'

import React, { useState } from 'react'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Box, Menu, MenuItem, Typography } from '@mui/material'

import type { HolidayPackageSort } from '@/src/constants/holidays'

interface PackageSortOption {
    value: HolidayPackageSort
    label: string
}

interface PackageSortControlProps {
    sortByLabel: string
    activeLabel: string
    activeValue: HolidayPackageSort
    options: PackageSortOption[]
    onChange: (value: HolidayPackageSort) => void
    isMobile: boolean
}

const PackageSortControl: React.FC<PackageSortControlProps> = ({
    sortByLabel,
    activeLabel,
    activeValue,
    options,
    onChange,
    isMobile
}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const fontSize = isMobile ? 12 : 13

    return (
        <>
            <Box
                component="button"
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={`${sortByLabel}: ${activeLabel}`}
                onClick={event => setAnchorEl(event.currentTarget)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    border: 1,
                    borderColor: open ? 'primary.main' : 'borders.default',
                    bgcolor: 'neutral.white',
                    borderRadius: 999,
                    px: 1.5,
                    py: 0.75,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    '&:hover': { borderColor: 'primary.main' }
                }}
            >
                {/* Phones drop the prefix so the pill fits beside the filter label; the aria-label keeps it. */}
                {!isMobile && (
                    <Typography sx={{ fontSize, color: 'textColors.secondary' }}>{sortByLabel}:</Typography>
                )}
                <Typography sx={{ fontSize, fontWeight: 600, color: 'textColors.primary' }}>{activeLabel}</Typography>
                <KeyboardArrowDownIcon
                    sx={{
                        fontSize: 18,
                        color: 'textColors.secondary',
                        transition: 'transform 0.2s',
                        transform: open ? 'rotate(180deg)' : 'none'
                    }}
                />
            </Box>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { mt: 0.5, minWidth: 160, borderRadius: 2 } } }}
            >
                {options.map(option => (
                    <MenuItem
                        key={option.value}
                        selected={option.value === activeValue}
                        onClick={() => {
                            onChange(option.value)
                            setAnchorEl(null)
                        }}
                        sx={{ fontSize: 14 }}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}

export { PackageSortControl }
export type { PackageSortControlProps, PackageSortOption }
export default PackageSortControl
